import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Pencil, Trash2, ChevronLeft, ChevronRight, Search, X, Loader2, AlertTriangle, Copy, Check, CheckCheck } from 'lucide-react';
import { receiptApi, accountingApi, type Receipt, type Account, type ReceiptDuplicate } from '../../lib/api';
import { useCompany } from '../CompanyProvider';
import { useConfirm } from '../ConfirmProvider';
import { useToast, errMsg } from '../ToastProvider';
import { Button, Card, EmptyState, ErrorNote, Field, PageHeader, euro } from '../ui';
import { uploadReceiptFiles } from './scan';

interface DraftItem {
  description: string;
  gross: string; // Euro als Text
  taxRate: string;
}

interface Draft {
  direction: 'expense' | 'income';
  date: string;
  vendor: string;
  invoiceNumber: string; // Rechnungs-/Belegnummer (Dublettenerkennung)
  description: string;
  category: string;
  accountNumber: string; // zugeordnetes SKR-Konto (Hauptkonto)
  isBewirtung: boolean;
  isPrivate: boolean; // private Ausgabe/Einnahme (nicht betrieblich, nicht gebucht)
  items: DraftItem[]; // Positionen (mind. eine)
}

const today = () => new Date().toISOString().slice(0, 10);
const emptyItem = (): DraftItem => ({ description: '', gross: '', taxRate: '19' });
const emptyDraft = (): Draft => ({
  direction: 'expense',
  date: today(),
  vendor: '',
  invoiceNumber: '',
  description: '',
  category: '',
  accountNumber: '',
  isBewirtung: false,
  isPrivate: false,
  items: [emptyItem()],
});

function toCents(s: string): number {
  const n = parseFloat(s.replace(',', '.'));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

// Haendlername -> Cache-Schluessel. MUSS mit dem Backend (normalizeMerchantKey)
// uebereinstimmen, damit die Logos zugeordnet werden koennen.
function merchantKey(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\bgmbh\b|\bag\b|\bug\b|\bkg\b|\bohg\b|\bgbr\b|\be\.?\s?k\.?\b|\bltd\b|\blimited\b|\binc\b|\bllc\b|\bs\.?\s?a\.?\b|\bse\b|\bco\b|\bplc\b|\bb\.?v\.?\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, '-');
}

// Stabile Farbe aus dem Namen (fuer den Initialen-Fallback).
const BADGE_COLORS = [
  'bg-blue-100 text-blue-700', 'bg-emerald-100 text-emerald-700', 'bg-amber-100 text-amber-700',
  'bg-violet-100 text-violet-700', 'bg-rose-100 text-rose-700', 'bg-cyan-100 text-cyan-700',
  'bg-indigo-100 text-indigo-700', 'bg-teal-100 text-teal-700',
];
function badgeColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return BADGE_COLORS[h % BADGE_COLORS.length]!;
}
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

/** Kreis-Badge: echtes Haendler-Logo, sonst Initialen mit stabiler Farbe. */
function MerchantBadge({ name, logo }: { name: string; logo?: string | null | undefined }): ReactElement {
  if (logo) {
    return (
      <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-white">
        <img src={logo} alt="" className="h-full w-full object-contain" />
      </span>
    );
  }
  return (
    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${badgeColor(name)}`}>
      {initials(name)}
    </span>
  );
}

// Wie viele Belege pro "Mehr anzeigen"-Schritt gerendert werden.
const PAGE_SIZE = 100;

// Haendlername normalisieren — MUSS mit normVendor im Backend
// (routes/receipts.ts) uebereinstimmen, damit Liste und Editor dieselben
// Dubletten melden.
function normVendor(v?: string | null): string {
  return (v ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '').trim();
}

/** Rechnungsnummer normalisiert, '' wenn keine da ist. */
function invNo(r: Receipt): string {
  return r.invoiceNumber?.trim().toLowerCase() ?? '';
}

/** Schluessel eines Beleg-Paares (sortiert), passend zum Backend. */
function pairKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

/** Ein Beleg mit Dublettenverdacht: Grund + die konkreten Partnerbelege. */
export interface DupInfo {
  reason: string;
  partnerIds: string[];
}

/**
 * Findet Belege, die zueinander moegliche Dubletten sind. Liefert Beleg-ID ->
 * Grund + Partner, damit der Nutzer es schon in der Liste sieht und nicht erst
 * beim Oeffnen des Belegs.
 *
 * Zwei Regeln:
 *  1) Gleiche Rechnungsnummer + gleicher Haendler -> starkes Signal.
 *  2) Gleicher Haendler + Betrag + Tag -> schwaches Signal, ABER nicht wenn
 *     beide Belege eine VERSCHIEDENE Rechnungsnummer tragen. Zwei
 *     unterschiedliche Nummern heissen: der Aussteller hat zwei Rechnungen
 *     erzeugt, also gab es zwei Vorgaenge (z.B. zwei Carsharing-Fahrten am
 *     selben Tag zum selben Preis) — keine Dublette.
 *
 * `dupOk` enthaelt die vom Nutzer abgenickten PAARE. Ausgeschlossen wird immer
 * nur die konkrete Kombination: laedt der Nutzer spaeter versehentlich einen
 * dritten, echt doppelten Beleg hoch, ist das ein neues Paar und wird gemeldet.
 * Deshalb paarweise und nicht als Flag am Beleg.
 */
function findDuplicateGroups(receipts: Receipt[], dupOk: Set<string>): Map<string, DupInfo> {
  const found = new Map<string, DupInfo>();
  // Ein Beleg gilt als verdaechtig, sobald mindestens EIN Partner uebrig
  // bleibt, der nicht abgenickt und nicht per Rechnungsnummer ausgeschlossen ist.
  const note = (a: Receipt, partners: Receipt[], reason: string, strong: boolean) => {
    const ids = partners.map((p) => p.id);
    if (ids.length === 0) return;
    const prev = found.get(a.id);
    // Starkes Signal (Regel 1) nicht durch das schwache ueberschreiben.
    if (prev && !strong) {
      prev.partnerIds = [...new Set([...prev.partnerIds, ...ids])];
      return;
    }
    found.set(a.id, { reason, partnerIds: [...new Set([...(prev?.partnerIds ?? []), ...ids])] });
  };

  // 1) Gleiche Rechnungsnummer + gleicher Haendler.
  const byInvoice = new Map<string, Receipt[]>();
  for (const r of receipts) {
    const inv = invNo(r);
    if (!inv) continue;
    const k = `${inv}|${normVendor(r.vendor)}`;
    const list = byInvoice.get(k);
    if (list) list.push(r);
    else byInvoice.set(k, [r]);
  }
  for (const items of byInvoice.values()) {
    if (items.length < 2) continue;
    for (const a of items) {
      // Betrag muss ebenfalls passen. Aus Alt-Buchhaltungen importierte Belege
      // tragen in invoiceNumber oft die laufende BUCHUNGSNUMMER des alten
      // Systems, die sich pro Haendler wiederholt. Ohne den Betragsvergleich
      // galten dadurch hunderte voellig verschiedene Belege als doppelt.
      const partners = items.filter(
        (b) => b.id !== a.id && b.grossCents === a.grossCents && !dupOk.has(pairKey(a.id, b.id)),
      );
      note(a, partners, 'Gleiche Rechnungsnummer, gleicher Händler und gleicher Betrag', true);
    }
  }

  // 2) Gleicher Haendler + Betrag + Tag.
  const byTriple = new Map<string, Receipt[]>();
  for (const r of receipts) {
    const vend = normVendor(r.vendor);
    if (!vend || r.grossCents <= 0) continue;
    const k = `${vend}|${r.grossCents}|${new Date(r.date).toDateString()}`;
    const list = byTriple.get(k);
    if (list) list.push(r);
    else byTriple.set(k, [r]);
  }
  for (const items of byTriple.values()) {
    if (items.length < 2) continue;
    for (const a of items) {
      const partners = items.filter((b) => {
        if (b.id === a.id) return false;
        if (dupOk.has(pairKey(a.id, b.id))) return false;
        // Beide mit Nummer und verschieden -> zwei echte Vorgaenge.
        if (invNo(a) && invNo(b) && invNo(a) !== invNo(b)) return false;
        return true;
      });
      note(a, partners, 'Gleicher Händler, Betrag und Datum', false);
    }
  }

  return found;
}

// Ein Autovervollständigungs-Vorschlag aus dem Datenbestand des Mandanten.
interface Suggestion {
  value: string;
  kind: 'vendor' | 'description' | 'invoice' | 'account' | 'category';
  count: number; // in wie vielen Belegen der Wert vorkommt
}

const SUGGESTION_LABEL: Record<Suggestion['kind'], string> = {
  vendor: 'Händler',
  description: 'Beschreibung',
  invoice: 'Rechnungsnr.',
  account: 'Konto',
  category: 'Kategorie',
};

/** Trefferstelle im Vorschlag fett hervorheben (Rest normal). */
function highlight(value: string, q: string): ReactElement {
  const at = q ? value.toLowerCase().indexOf(q.trim().toLowerCase()) : -1;
  if (at < 0) return <>{value}</>;
  const len = q.trim().length;
  return (
    <>
      {value.slice(0, at)}
      <strong className="font-semibold text-blue-700 dark:text-blue-300">{value.slice(at, at + len)}</strong>
      {value.slice(at + len)}
    </>
  );
}

/**
 * Suchfeld mit Autovervollständigung. Die Vorschläge stammen aus den Belegen
 * des Mandanten (siehe `suggestions` im Elternteil) und sind per Maus oder
 * Tastatur (↑/↓/Enter/Esc) wählbar.
 */
function ReceiptSearch({
  value,
  onChange,
  suggestions,
  onPick,
}: {
  value: string;
  onChange: (v: string) => void;
  suggestions: Suggestion[];
  onPick: (v: string) => void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1); // -1 = nur der getippte Text
  const boxRef = useRef<HTMLDivElement>(null);

  // Klick ausserhalb schliesst die Vorschlagsliste.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const list = open ? suggestions : [];

  function pick(v: string) {
    onPick(v);
    setOpen(false);
    setActive(-1);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      if (list.length > 0) setOpen(false);
      else onChange('');
      return;
    }
    if (list.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1 >= list.length ? -1 : i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i <= -1 ? list.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      if (active >= 0 && list[active]) {
        e.preventDefault();
        pick(list[active]!.value);
      } else {
        setOpen(false);
      }
    }
  }

  return (
    <div ref={boxRef} className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
      <input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setActive(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        type="search"
        role="combobox"
        aria-expanded={list.length > 0}
        aria-autocomplete="list"
        placeholder="Beleg suchen – Händler, Beschreibung, Rechnungsnr., Betrag …"
        className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-9 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-900"
      />
      {value && (
        <button
          type="button"
          onClick={() => { onChange(''); setOpen(false); setActive(-1); }}
          aria-label="Suche leeren"
          className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700"
        >
          <X className="h-4 w-4" />
        </button>
      )}
      {list.length > 0 && (
        <ul className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
          {list.map((s, i) => (
            <li key={`${s.kind}-${s.value}`}>
              <button
                type="button"
                // onMouseDown, damit der Klick nicht am Blur des Feldes verpufft.
                onMouseDown={(e) => { e.preventDefault(); pick(s.value); }}
                onMouseEnter={() => setActive(i)}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition ${
                  i === active ? 'bg-blue-50 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700/60'
                }`}
              >
                <span className="min-w-0 flex-1 truncate text-gray-900 dark:text-gray-100">{highlight(s.value, value)}</span>
                <span className="shrink-0 text-xs text-gray-400">{SUGGESTION_LABEL[s.kind]}</span>
                <span className="shrink-0 rounded-full bg-gray-100 px-1.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">{s.count}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Netto aus Brutto + Satz (Cent).
function grossToNetC(grossCents: number, rate: number): number {
  if (!rate) return grossCents;
  return Math.round(grossCents / (1 + rate / 100));
}

// Restdauer menschenlesbar: Sekunden bzw. "M:SS min" ab 60 s.
function formatEta(sec: number): string {
  if (sec < 60) return `${sec} Sek.`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')} Min.`;
}

export default function Receipts(): ReactElement {
  const { active } = useCompany();
  // KI-Belegerkennung ist erst ab Plus enthalten (Starter erfasst manuell).
  // Muss mit plans.ts im Backend uebereinstimmen.
  const canUseAi = active ? active.plan !== 'starter' : false;
  // ?receipt=<id> oeffnet einen bestimmten Beleg direkt (z.B. aus der
  // Jahresabschluss-Checkliste). ?q=<text> setzt die Suche.
  const [searchParams, setSearchParams] = useSearchParams();
  const confirm = useConfirm();
  const toast = useToast();
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [editId, setEditId] = useState<string | null>(null); // null = neuer Beleg
  const [supplierLinked, setSupplierLinked] = useState(false); // Beleg hat verknuepften Lieferanten
  const [fileUrl, setFileUrl] = useState<string | null>(null); // Original-Datei im Editor
  const [fileMime, setFileMime] = useState<string | null>(null); // steuert Bild vs. PDF-Vorschau
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Upload-Fortschritt: prozentualer Anteil (0..100) + geschaetzte Restsekunden.
  // null = kein Upload aktiv. Der Kunde muss NUR waehrend des Uploads warten;
  // die KI-Erkennung laeuft danach server-side (Live-Polling zeigt sie an).
  const [uploadProgress, setUploadProgress] = useState<{ pct: number; etaSec: number | null } | null>(null);
  // true, solange Dateien ueber der Seite schweben (Drag & Drop-Hervorhebung).
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // Angezeigte Position als "YYYY-MM" (null = noch nicht initialisiert -> neuester
  // Monat mit Belegen wird gesetzt, sobald die Belege geladen sind). Im Jahres-
  // Modus zaehlt nur der Jahresteil.
  const [cursor, setCursor] = useState<string | null>(null);
  // Zeitraum-Modus: 'month' = ein Monat (Standard), 'year' = ganzes Jahr des
  // Cursors, 'all' = alle Belege ohne Zeitfilter. Kundenwunsch: nicht mehr
  // Monat fuer Monat durchklicken muessen.
  const [range, setRange] = useState<'month' | 'year' | 'all'>('month');
  // Richtungsfilter: '' = alle, 'income' = nur Einnahmen, 'expense' = nur Ausgaben.
  const [filterDir, setFilterDir] = useState<'' | 'income' | 'expense'>('');
  // Freitextsuche ueber Haendler, Beschreibung, Rechnungsnr., Konto, Betrag.
  const [query, setQuery] = useState('');
  // Statusfilter aus dem Banner: 'pending' = nur laufende KI-Erkennung,
  // 'review' = nur nicht erkannte/fehlgeschlagene, 'duplicate' = mögliche
  // Dubletten. Ignoriert den Zeitraum, damit man die betroffenen Belege
  // garantiert sieht (sie koennen in jedem Monat liegen).
  const [statusFilter, setStatusFilter] = useState<'' | 'pending' | 'review' | 'duplicate'>('');
  // Vom Nutzer abgenickte "keine Dublette"-Paare als Set von pairKey(a,b).
  const [dupOk, setDupOk] = useState<Set<string>>(new Set());
  // Anzeigelimit: bei 'Alle'/Jahr koennen tausende Belege zusammenkommen. Wir
  // rendern nur die ersten N und laden auf Klick nach (rein clientseitig).
  const [limit, setLimit] = useState(PAGE_SIZE);
  // Haendler-Logos: normalisierter Schluessel -> Data-URL | null (Initialen).
  const [logos, setLogos] = useState<Record<string, string | null>>({});
  // Live-Dublettenwarnung im Editor (vor dem Speichern).
  const [dupWarning, setDupWarning] = useState<ReceiptDuplicate[]>([]);

  // Beleg im Editor öffnen (KI-Ergebnis prüfen/korrigieren).
  function openEdit(r: Receipt) {
    setError(null);
    setEditId(r.id);
    setSupplierLinked(!!r.supplierId);
    // Positionen aus dem Beleg übernehmen; falls keine -> eine Zeile aus dem Gesamtbetrag.
    const items: DraftItem[] =
      r.items && r.items.length > 0
        ? r.items.map((it) => ({
            description: it.description,
            gross: (it.grossCents / 100).toFixed(2).replace('.', ','),
            taxRate: String(Number(it.taxRate)),
          }))
        : [
            {
              description: r.description ?? '',
              gross: (r.grossCents / 100).toFixed(2).replace('.', ','),
              taxRate: r.taxRate ? String(Number(r.taxRate)) : '19',
            },
          ];
    setDraft({
      direction: r.direction,
      date: new Date(r.date).toISOString().slice(0, 10),
      vendor: r.vendor ?? '',
      invoiceNumber: r.invoiceNumber ?? '',
      description: r.description ?? '',
      category: r.category ?? '',
      accountNumber: r.accountNumber ?? '',
      isBewirtung: r.isBewirtung ?? false,
      isPrivate: r.isPrivate ?? false,
      items,
    });
    // Original-Datei (falls vorhanden) als Vorschau laden. Den Typ mitfuehren:
    // PDFs koennen NICHT in ein <img> — die brauchen ein <iframe>.
    setFileUrl(null);
    setFileMime(r.fileMime ?? null);
    if (r.fileMime && active) {
      receiptApi
        .file(active.id, r.id)
        .then((b) => {
          setFileUrl(URL.createObjectURL(b));
          // Der Blob kennt seinen Typ zuverlaessiger als das DB-Feld.
          if (b.type) setFileMime(b.type);
        })
        .catch(() => undefined);
    }
  }

  function startNew() {
    setError(null);
    setEditId(null);
    setSupplierLinked(false);
    if (fileUrl) URL.revokeObjectURL(fileUrl);
    setFileUrl(null);
    setFileMime(null);
    setDraft(emptyDraft());
  }

  function closeEditor() {
    if (fileUrl) URL.revokeObjectURL(fileUrl);
    setFileUrl(null);
    setFileMime(null);
    setDraft(null);
    setEditId(null);
  }

  // silent: fuer Hintergrund-Polling ohne Lade-Spinner (sonst flackert die Liste).
  async function load(silent = false) {
    if (!active) return;
    if (!silent) setLoading(true);
    try {
      setReceipts(await receiptApi.list(active.id));
    } finally {
      if (!silent) setLoading(false);
    }
    // Abgenickte "keine Dublette"-Paare NACH der Liste und bewusst entkoppelt:
    // scheitert der Aufruf, bleibt die Belegliste trotzdem sichtbar (der
    // Hinweis ist dann nur kurzzeitig zu streng, statt die Seite leer zu lassen).
    try {
      setDupOk(new Set((await receiptApi.dupOkPairs(active.id)).map(([a, b]) => pairKey(a, b))));
    } catch {
      /* Hinweis bleibt aktiv — kein harter Fehler. */
    }
  }

  // Mehrere Dateien hochladen -> KI-Erkennung im Hintergrund.
  // Gemeinsamer Pfad fuer Datei-Dialog UND Drag & Drop.
  async function uploadFiles(list: File[]) {
    if (!active || list.length === 0) return;
    setUploading(true);
    setUploadProgress({ pct: 0, etaSec: null });
    const startedAt = performance.now();
    try {
      const created = await uploadReceiptFiles(active.id, list, (sent, total) => {
        const pct = total > 0 ? Math.round((sent / total) * 100) : 0;
        // Restdauer aus bisheriger Uploadrate schaetzen (Bytes/ms -> Restbytes).
        const elapsed = performance.now() - startedAt;
        const rate = sent / elapsed; // Bytes pro ms
        const etaSec = rate > 0 && sent < total ? Math.ceil((total - sent) / rate / 1000) : 0;
        setUploadProgress({ pct, etaSec });
      });
      // Upload fertig -> jetzt laeuft die serverseitige KI-Erkennung (kein Warten).
      setUploadProgress(null);
      toast.success(`${created} Beleg(e) hochgeladen – Erkennung läuft im Hintergrund.`);
      await load();
      // Ab hier zieht das Live-Polling (useEffect unten) den Status automatisch
      // nach, solange Belege auf 'pending' stehen — kein fester Timer noetig.
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
      setUploadProgress(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function onUpload(e: React.ChangeEvent<HTMLInputElement>) {
    await uploadFiles(Array.from(e.target.files ?? []));
  }

  // Nur das nehmen, was die API akzeptiert (Bilder + PDF) — der Rest wird
  // beim Reinziehen ignoriert, statt den ganzen Upload scheitern zu lassen.
  // iOS/Safari liefert bei HEIC oft KEINEN oder einen generischen MIME-Type,
  // deshalb zusaetzlich die Dateiendung pruefen (sonst faellt gerade das
  // iPhone-Foto durchs Raster). Den echten Typ bestimmt der Server aus den
  // Magic Bytes.
  const SUPPORTED_EXT = /\.(jpe?g|png|gif|webp|avif|heic|heif|tiff?|pdf)$/i;
  const isSupported = (f: File) =>
    f.type.startsWith('image/') || f.type === 'application/pdf' || SUPPORTED_EXT.test(f.name);

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    dragDepth.current = 0;
    setDragOver(false);
    if (uploading || draft) return;
    if (!canUseAi) {
      toast.info('Die KI-Belegerkennung ist ab dem Plus-Tarif verfügbar. Du kannst Belege manuell erfassen.');
      return;
    }
    const all = Array.from(e.dataTransfer.files ?? []);
    if (all.length === 0) return;
    const files = all.filter(isSupported);
    if (files.length === 0) {
      toast.error('Bitte Bilder (JPG/PNG) oder PDF-Dateien ablegen.');
      return;
    }
    if (files.length < all.length) {
      toast.info(`${all.length - files.length} Datei(en) übersprungen – nur Bilder und PDFs werden erkannt.`);
    }
    void uploadFiles(files);
  }

  // dragenter/-over feuern auch beim Wechsel zwischen Kindelementen. Ein
  // Zaehler verhindert, dass die Hervorhebung dabei flackert.
  const dragDepth = useRef(0);

  function onDragEnter(e: React.DragEvent) {
    // Nur auf echte Datei-Drags reagieren (nicht auf markierten Text o.ae.).
    if (!Array.from(e.dataTransfer.types ?? []).includes('Files')) return;
    // Bei offenem Editor nicht stoeren — dort zaehlt der Beleg im Formular.
    if (draft) return;
    // Ohne KI-Erkennung gibt es keinen Upload-Pfad -> gar nicht erst anbieten.
    if (!canUseAi) return;
    dragDepth.current += 1;
    setDragOver(true);
  }

  function onDragLeave() {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
  }
  useEffect(() => {
    void load();
    if (active) accountingApi.accounts(active.id).then(setAccounts).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Faellt eine Datei NEBEN den Drop-Bereich, wuerde der Browser sie im Tab
  // oeffnen und die App verlassen (angefangene Eingaben weg). Deshalb global
  // abfangen und ins Leere laufen lassen.
  useEffect(() => {
    const swallow = (e: DragEvent) => {
      if (Array.from(e.dataTransfer?.types ?? []).includes('Files')) e.preventDefault();
    };
    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallow);
    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallow);
    };
  }, []);

  // Live-Polling: solange mindestens ein Beleg noch in der KI-Erkennung steckt
  // ('pending'), im Hintergrund nachladen bis alle fertig sind. Der Nutzer sieht
  // Vendor/Betrag automatisch erscheinen — ohne manuellen Reload. Stoppt von
  // selbst, sobald kein 'pending' mehr da ist (dependency wechselt auf false).
  // Zaehlung ueber ALLE Belege, nicht nur die sichtbaren: neu hochgeladene
  // Belege liegen im aktuellen Monat, der Nutzer kann aber in einem anderen
  // Zeitraum stehen — sonst waere die Pipeline unsichtbar.
  const pendingCount = receipts.filter((r) => r.ocrStatus === 'pending').length;
  const reviewCount = receipts.filter((r) => r.ocrStatus === 'needs_review' || r.ocrStatus === 'failed').length;
  // Moegliche Dubletten ueber ALLE Belege (nicht nur den Zeitraum) — dieselbe
  // Regel wie im Backend. Direkt in der Liste sichtbar, nicht erst im Editor.
  const dupFlags = findDuplicateGroups(receipts, dupOk);
  // Tiefer Link (?receipt=<id>): den Beleg oeffnen, sobald die Liste da ist.
  // Wird u.a. aus der Jahresabschluss-Checkliste verlinkt, damit der Nutzer
  // den beanstandeten Beleg sofort sieht statt ihn zu suchen. Der Parameter
  // wird danach entfernt, damit ein Reload nicht erneut aufspringt.
  const openedDeepLink = useRef(false);
  useEffect(() => {
    const wanted = searchParams.get('receipt');
    if (!wanted || openedDeepLink.current || receipts.length === 0) return;
    const treffer = receipts.find((r) => r.id === wanted);
    openedDeepLink.current = true;
    if (treffer) {
      // Zeitraum aufheben, sonst waere der Beleg hinter dem Monatsfilter versteckt.
      setRange('all');
      openEdit(treffer);
    } else {
      toast.error('Der verlinkte Beleg wurde nicht gefunden.');
    }
    const rest = new URLSearchParams(searchParams);
    rest.delete('receipt');
    setSearchParams(rest, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipts, searchParams]);

  const hasPending = pendingCount > 0;
  useEffect(() => {
    if (!active || !hasPending) return;
    const id = setInterval(() => { void load(true); }, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, hasPending]);

  // Nur buchbare Erfolgskonten (Aufwand/Erlös) zur Auswahl, je nach Belegrichtung.
  // Privatkonten (1800/1890) sind KEIN gueltiges Hauptkonto eines Belegs: privat
  // bezahlte Betriebsausgaben werden aufs Aufwandskonto gebucht, die Privateinlage
  // ist nur das Gegenkonto (passiert automatisch in bookReceipt, wenn keine Bank).
  // Ausnahme: 0480 (GWG-Sammelkonto) ist ein Aktivkonto, aber ein gueltiges
  // Belegkonto — ein geringwertiges Wirtschaftsgut wird darauf aktiviert und
  // im selben Jahr sofort abgeschrieben (§ 6 Abs. 2 EStG). Ohne diese Ausnahme
  // liesse sich ein GWG-Beleg gar nicht zuordnen.
  const expenseAccounts = accounts.filter((a) => a.type === 'expense' || a.number === '0480');
  const revenueAccounts = accounts.filter((a) => a.type === 'revenue');

  const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

  // "YYYY-MM" eines Belegs (lokale Zeit).
  const monthKey = (iso: string) => {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  // Alle Monate MIT Belegen, absteigend (neuester zuerst). Steuert Vor/Zurück
  // (leere Monate werden übersprungen) und die Initialposition.
  const monthsWithReceipts = Array.from(new Set(receipts.map((r) => monthKey(r.date)))).sort((a, b) =>
    b.localeCompare(a),
  );

  // Beim ersten Laden / Mandantenwechsel auf den neuesten belegten Monat setzen,
  // der NICHT in der Zukunft liegt. Vorausgeplante Belege (z.B. kuenftige
  // Energie-Abschlaege bis ins Folgejahr) sollen die Ansicht nicht in die Zukunft
  // springen lassen — sie bleiben per Vor-Pfeil erreichbar. Liegen ausnahmsweise
  // ALLE Belege in der Zukunft, wird der frueheste davon gewaehlt.
  useEffect(() => {
    if (monthsWithReceipts.length === 0) { setCursor(null); return; }
    if (cursor && monthsWithReceipts.includes(cursor)) return;
    const thisMonth = new Date().toISOString().slice(0, 7); // "YYYY-MM"
    const notFuture = monthsWithReceipts.find((m) => m <= thisMonth);
    setCursor(notFuture ?? monthsWithReceipts[monthsWithReceipts.length - 1]!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipts]);

  // Alle Jahre MIT Belegen, absteigend — Pendant zu monthsWithReceipts fuer
  // den Jahres-Modus.
  const yearsWithReceipts = Array.from(new Set(monthsWithReceipts.map((m) => m.slice(0, 4)))).sort((a, b) =>
    b.localeCompare(a),
  );

  // Im Jahres-Modus navigiert der Cursor jahresweise, im Monats-Modus monatsweise.
  // 'all' kennt keine Navigation (Pfeile sind dort ausgeblendet).
  const steps = range === 'year' ? yearsWithReceipts : monthsWithReceipts;
  const cursorStep = cursor ? (range === 'year' ? cursor.slice(0, 4) : cursor) : '';
  const cursorIdx = cursorStep ? steps.indexOf(cursorStep) : -1;
  const hasOlder = cursorIdx >= 0 && cursorIdx < steps.length - 1; // ältere Belege
  const hasNewer = cursorIdx > 0; // neuere Belege
  // Beim Jahressprung auf den neuesten belegten Monat des Zieljahres setzen,
  // damit ein Wechsel zurueck auf 'Monat' nicht in einem leeren Monat landet.
  const jumpTo = (step: string) => {
    if (range !== 'year') { setCursor(step); return; }
    setCursor(monthsWithReceipts.find((m) => m.startsWith(step)) ?? `${step}-01`);
  };
  const goOlder = () => { if (hasOlder) jumpTo(steps[cursorIdx + 1]!); };
  const goNewer = () => { if (hasNewer) jumpTo(steps[cursorIdx - 1]!); };

  // Anzeige-Label: "Juni 2026" (Monat), "2026" (Jahr) bzw. "Alle Belege".
  const cursorLabel =
    range === 'all'
      ? 'Alle Belege'
      : cursor
        ? range === 'year'
          ? cursor.slice(0, 4)
          : `${MONTHS[Number(cursor.slice(5, 7)) - 1]} ${cursor.slice(0, 4)}`
        : '';

  // Suchbegriff -> einzelne Wörter (UND-Verknüpfung, Reihenfolge egal).
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);

  // Durchsuchbarer Text eines Belegs: alles, wonach ein Kunde suchen würde.
  const haystack = (r: Receipt) =>
    [
      r.vendor,
      r.description,
      r.invoiceNumber,
      r.category,
      r.accountNumber,
      new Date(r.date).toLocaleDateString('de-DE'),
      (r.grossCents / 100).toFixed(2).replace('.', ','),
      ...(r.items ?? []).map((it) => it.description),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

  // Autovervollständigung: Vorschläge ausschliesslich aus dem eigenen
  // Datenbestand (Händler, Beschreibungen, Rechnungsnummern, Konten aller
  // geladenen Belege — nicht nur des angezeigten Monats). Treffer am Wortanfang
  // zuerst, danach Teiltreffer; jeweils mit Anzahl der Belege.
  const suggestions: Suggestion[] = (() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    const counts = new Map<string, Suggestion>();
    const add = (label: string, kind: Suggestion['kind']) => {
      const v = label.trim();
      if (!v) return;
      const k = `${kind} ${v.toLowerCase()}`;
      const hit = counts.get(k);
      if (hit) hit.count += 1;
      else counts.set(k, { value: v, kind, count: 1 });
    };
    for (const r of receipts) {
      if (r.vendor) add(r.vendor, 'vendor');
      if (r.description) add(r.description, 'description');
      if (r.invoiceNumber) add(r.invoiceNumber, 'invoice');
      if (r.accountNumber) add(`${r.accountNumber} ${r.category ?? ''}`.trim(), 'account');
      else if (r.category) add(r.category, 'category');
    }
    const matches = [...counts.values()]
      .map((s) => ({ s, at: s.value.toLowerCase().indexOf(q) }))
      .filter((x) => x.at >= 0)
      // Wortanfang schlaegt Mitte; dann haeufigere zuerst, dann alphabetisch.
      .sort((a, b) => {
        const aStart = a.at === 0 || /\W/.test(a.s.value[a.at - 1] ?? '');
        const bStart = b.at === 0 || /\W/.test(b.s.value[b.at - 1] ?? '');
        if (aStart !== bStart) return aStart ? -1 : 1;
        if (a.s.count !== b.s.count) return b.s.count - a.s.count;
        return a.s.value.localeCompare(b.s.value, 'de');
      })
      .slice(0, 8)
      .map((x) => x.s);
    // Exakt eingetippter Begriff braucht keinen eigenen Vorschlag.
    return matches.filter((s) => s.value.toLowerCase() !== q);
  })();

  // Sichtbare Belege: Zeitraum + Richtung + Suche. Bei aktiver Suche gilt der
  // Zeitraum weiter — der Umschalter steht auf "Alle", wenn man global sucht.
  const visible = receipts.filter((r) => {
    // Statusfilter hebt den Zeitraum auf: die betroffenen Belege koennen in
    // jedem Monat liegen und muessen alle auf einmal sichtbar sein.
    if (statusFilter === 'pending' && r.ocrStatus !== 'pending') return false;
    if (statusFilter === 'review' && r.ocrStatus !== 'needs_review' && r.ocrStatus !== 'failed') return false;
    if (statusFilter === 'duplicate' && !dupFlags.has(r.id)) return false;
    if (!statusFilter) {
      if (range === 'month' && cursor && monthKey(r.date) !== cursor) return false;
      if (range === 'year' && cursor && !monthKey(r.date).startsWith(cursor.slice(0, 4))) return false;
    }
    if (filterDir && r.direction !== filterDir) return false;
    if (terms.length > 0) {
      const h = haystack(r);
      if (!terms.every((t) => h.includes(t))) return false;
    }
    return true;
  });

  // Bei 'Alle'/Jahr neueste zuerst — sonst wäre die Reihenfolge über Monate
  // hinweg beliebig. Der Server liefert bereits sortiert; hier zur Sicherheit
  // auf einer Kopie erneut sortieren.
  const visibleSorted =
    statusFilter === 'duplicate'
      ? // Dubletten paarweise untereinander: erst Haendler+Betrag, dann Datum.
        [...visible].sort(
          (a, b) =>
            normVendor(a.vendor).localeCompare(normVendor(b.vendor)) ||
            a.grossCents - b.grossCents ||
            new Date(a.date).getTime() - new Date(b.date).getTime(),
        )
      : range === 'month' && terms.length === 0 && !statusFilter
        ? visible
        : [...visible].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // Nur die ersten `limit` Belege rendern; der Rest kommt per "Mehr anzeigen".
  const shown = visibleSorted.slice(0, limit);
  const hiddenCount = visibleSorted.length - shown.length;

  // Filterwechsel -> wieder oben anfangen (sonst bliebe ein hohes Limit stehen).
  useEffect(() => { setLimit(PAGE_SIZE); }, [range, cursor, filterDir, query, statusFilter]);

  // Saldo (Einnahmen − Ausgaben) der aktuellen Auswahl (Zeitraum + Filter + Suche).
  const visibleSum = visible.reduce(
    (acc, r) => acc + (r.direction === 'income' ? r.grossCents : -r.grossCents),
    0,
  );

  // Haendler-Logos fuer die sichtbaren Belege nachladen (global gecacht im
  // Backend). Nur noch nicht bekannte Haendler anfragen; ein Eintrag (auch null)
  // im Cache verhindert erneute Anfragen. Das Backend loest pro Aufruf wenige
  // Cache-Misses auf -> der Effekt laeuft bei Bedarf mehrfach (logos waechst).
  useEffect(() => {
    if (!active) return;
    const wanted = new Map<string, string>(); // key -> Originalname
    // Nur fuer tatsaechlich gerenderte Belege (bei 'Alle' waeren es sonst
    // Logo-Anfragen fuer die gesamte Historie).
    for (const r of shown) {
      const name = r.vendor?.trim();
      if (!name) continue;
      const k = merchantKey(name);
      if (k && !(k in logos) && !wanted.has(k)) wanted.set(k, name);
    }
    if (wanted.size === 0) return;
    let cancelled = false;
    receiptApi
      .merchantLogos(active.id, [...wanted.values()])
      .then((res) => {
        if (cancelled) return;
        // Nur tatsaechlich zurueckgegebene Keys uebernehmen. Das Backend liefert
        // alle Cache-Treffer sofort + wenige neu aufgeloeste. Nicht enthaltene
        // Keys bleiben offen und werden im naechsten Durchlauf erneut angefragt.
        // Liefert der Aufruf gar nichts Neues, waechst `logos` nicht -> Stopp.
        const fresh = Object.keys(res).filter((k) => !(k in logos));
        if (fresh.length === 0) return;
        setLogos((prev) => ({ ...prev, ...res }));
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, shown, logos]);

  // Live-Dublettenpruefung im Editor (debounced). Prueft, sobald genug Merkmale
  // da sind (Rechnungsnr. ODER Haendler+Betrag). Beim Bearbeiten eigenen Beleg
  // ausschliessen. Warnt nur — blockiert das Speichern nicht.
  const dupGross = draft ? draft.items.reduce((s, it) => s + toCents(it.gross), 0) : 0;
  useEffect(() => {
    if (!active || !draft) { setDupWarning([]); return; }
    const inv = draft.invoiceNumber.trim();
    const vendor = draft.vendor.trim();
    // Zu wenig Information -> nicht pruefen.
    if (!inv && !(vendor && dupGross > 0)) { setDupWarning([]); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      receiptApi
        .checkDuplicate(active.id, {
          invoiceNumber: inv || null,
          vendor: vendor || null,
          grossCents: dupGross || undefined,
          date: draft.date,
          excludeId: editId ?? undefined,
        })
        .then((dups) => { if (!cancelled) setDupWarning(dups); })
        .catch(() => { if (!cancelled) setDupWarning([]); });
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, draft?.invoiceNumber, draft?.vendor, draft?.date, dupGross, editId]);

  // Live-Summen aus den Positionen des aktuellen Drafts.
  function draftTotals(d: Draft) {
    let gross = 0;
    let net = 0;
    for (const it of d.items) {
      const g = toCents(it.gross);
      gross += g;
      net += grossToNetC(g, Number(it.taxRate) || 0);
    }
    const tax = gross - net;
    // Bewirtung: 70% des Netto abziehbar, 30% nicht; Vorsteuer voll.
    const deductibleNet = Math.round(net * 0.7);
    return { gross, net, tax, deductibleNet, nonDeductibleNet: net - deductibleNet };
  }

  async function save() {
    if (!active || !draft) return;
    setError(null);
    const totals = draftTotals(draft);
    if (totals.gross <= 0) {
      setError(new Error('Bitte mindestens eine Position mit Betrag größer 0 angeben.'));
      return;
    }
    setBusy(true);
    // Dominanter Steuersatz (höchster) für die Beleg-Hauptzeile.
    const mainRate = draft.items.reduce((m, it) => Math.max(m, Number(it.taxRate) || 0), 0);
    const payload = {
      direction: draft.direction,
      date: draft.date,
      vendor: draft.vendor || undefined,
      description: draft.description || undefined,
      invoiceNumber: draft.invoiceNumber.trim() || null,
      grossCents: totals.gross,
      taxRate: mainRate,
      category: draft.category || undefined,
      accountNumber: draft.accountNumber || null,
      isBewirtung: draft.isBewirtung,
      isPrivate: draft.isPrivate,
      items: draft.items
        .filter((it) => toCents(it.gross) > 0)
        .map((it) => ({
          description: it.description || 'Position',
          quantity: 1,
          grossCents: toCents(it.gross),
          taxRate: Number(it.taxRate) || 0,
        })),
    };
    try {
      if (editId) {
        await receiptApi.update(active.id, editId, payload);
        toast.success('Beleg gespeichert.');
      } else {
        const { duplicates } = await receiptApi.create(active.id, payload);
        if (duplicates.length > 0) {
          const dup = duplicates[0]!;
          toast.error(`Achtung: möglicherweise eine Dublette (${dup.reason}). Beleg wurde trotzdem gespeichert – bei Bedarf löschen.`);
        } else {
          toast.success('Beleg erfasst.');
        }
      }
      closeEditor();
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  /**
   * "Ist kein Duplikat" fuer einen Beleg + seine aktuellen Partner abnicken.
   * Gespeichert wird die KOMBINATION, nicht der Beleg: laedt der Nutzer spaeter
   * versehentlich denselben Beleg noch einmal hoch, ist das ein neues Paar und
   * wird wieder gemeldet. Optimistisch — bei Fehler wird zurueckgedreht.
   */
  async function markNotDuplicate(id: string) {
    if (!active) return;
    const partners = dupFlags.get(id)?.partnerIds ?? [];
    if (partners.length === 0) return;
    const ids = [id, ...partners];
    const keys = partners.map((p) => pairKey(id, p));
    const before = dupOk;
    setDupOk((prev) => new Set([...prev, ...keys]));
    try {
      await receiptApi.setDupOk(active.id, ids, true);
      toast.success(
        partners.length === 1
          ? 'Als „kein Duplikat“ gemerkt. Ein erneuter Upload desselben Belegs wird weiter erkannt.'
          : `Für ${ids.length} Belege als „kein Duplikat“ gemerkt.`,
      );
    } catch (e) {
      setDupOk(before);
      toast.error(errMsg(e));
    }
  }

  /** Alle abgenickten Paare verwerfen -> alles wird wieder geprueft. */
  async function resetDupOk() {
    if (!active || dupOk.size === 0) return;
    const ok = await confirm({
      title: 'Dublettenprüfung zurücksetzen?',
      message: `${dupOk.size} als „kein Duplikat“ gemerkte Kombination(en) werden verworfen. Alle Belege werden danach wieder auf Dubletten geprüft.`,
      confirmLabel: 'Zurücksetzen',
    });
    if (!ok) return;
    const before = dupOk;
    const groups = [...before].map((k) => k.split('|') as [string, string]);
    setDupOk(new Set());
    try {
      // Paarweise zuruecknehmen (der Endpunkt erwartet je Aufruf eine Gruppe).
      for (const [a, b] of groups) await receiptApi.setDupOk(active.id, [a, b], false);
      toast.info('Dublettenprüfung zurückgesetzt.');
    } catch (e) {
      setDupOk(before);
      toast.error(errMsg(e));
    }
  }

  async function remove(id: string) {
    if (!active) return;
    const ok = await confirm({ title: 'Beleg löschen?', message: 'Dieser Beleg wird dauerhaft entfernt.', confirmLabel: 'Löschen', danger: true });
    if (!ok) return;
    await receiptApi.remove(active.id, id);
    toast.info('Beleg gelöscht.');
    await load();
  }

  return (
    <div
      className="relative mx-auto min-h-[70vh] max-w-4xl"
      onDragEnter={onDragEnter}
      onDragOver={(e) => {
        // Ohne preventDefault oeffnet der Browser die Datei statt sie abzulegen.
        if (Array.from(e.dataTransfer.types ?? []).includes('Files')) e.preventDefault();
      }}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* Drag & Drop: Overlay ueber der ganzen Seite, damit man die Belege
          irgendwo fallen lassen kann — nicht nur auf einer kleinen Flaeche. */}
      {dragOver && (
        <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center rounded-2xl border-2 border-dashed border-blue-500 bg-blue-50/90 backdrop-blur-[1px] dark:bg-blue-950/80">
          <div className="text-center">
            <div className="text-4xl">📥</div>
            <p className="mt-2 text-lg font-semibold text-blue-900 dark:text-blue-100">
              {uploading ? 'Upload läuft …' : 'Belege hier ablegen'}
            </p>
            <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
              {uploading ? 'Bitte warten, bis der aktuelle Upload fertig ist.' : 'Bilder (JPG/PNG) oder PDF – mehrere gleichzeitig möglich.'}
            </p>
          </div>
        </div>
      )}

      <PageHeader
        title="Belege"
        subtitle={
          canUseAi
            ? 'Beleg fotografieren, hochladen oder einfach hierher ziehen – die KI erkennt Betrag, Datum & Händler automatisch.'
            : 'Erfasse deine Belege – Rechnungen, Quittungen und Einnahmen.'
        }
        action={
          <div className="flex gap-2">
            {/* .heic/.heif explizit: manche Browser (Windows/Chrome) blenden
                iPhone-Fotos sonst im Dateidialog aus, weil sie image/heic nicht
                als Bildtyp kennen. */}
            <input
              ref={fileRef}
              type="file"
              accept="image/*,.heic,.heif,application/pdf"
              multiple
              onChange={onUpload}
              className="hidden"
            />
            {/* Upload = KI-Erkennung -> im Starter-Tarif nicht anbieten. */}
            {canUseAi ? (
              <>
                <Button onClick={() => fileRef.current?.click()} busy={uploading}>📷 Belege hochladen</Button>
                <Button variant="secondary" onClick={startNew}>+ Manuell</Button>
              </>
            ) : (
              <Button onClick={startNew}>+ Neuer Beleg</Button>
            )}
          </div>
        }
      />

      {/* Upload-Fortschritt: nur der Upload laesst den Kunden warten (seine Leitung).
          Die KI-Erkennung danach laeuft server-side -> hier bewusst nicht mitgezaehlt. */}
      {uploadProgress && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/40">
          <div className="mb-2 flex items-center justify-between text-sm font-medium text-blue-800 dark:text-blue-200">
            <span>
              {uploadProgress.pct >= 100
                ? 'Upload abgeschlossen – Belege werden erkannt …'
                : `Belege werden hochgeladen … ${uploadProgress.pct}%`}
            </span>
            {uploadProgress.pct < 100 && uploadProgress.etaSec != null && (
              <span className="tabular-nums text-blue-600 dark:text-blue-300">
                {uploadProgress.etaSec > 0 ? `noch ca. ${formatEta(uploadProgress.etaSec)}` : 'fast fertig …'}
              </span>
            )}
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-blue-200 dark:bg-blue-900">
            <div
              className="h-full rounded-full bg-blue-600 transition-all duration-200 ease-out dark:bg-blue-500"
              style={{ width: `${uploadProgress.pct}%` }}
            />
          </div>
        </div>
      )}

      {/* Starter: erklaeren, warum es keinen Upload gibt — mit Weg zum Upgrade. */}
      {!loading && !canUseAi && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-900 dark:bg-blue-950/40">
          <span className="min-w-0 text-sm text-blue-900 dark:text-blue-100">
            <span className="font-semibold">Belege automatisch erkennen lassen?</span>{' '}
            <span className="text-blue-700 dark:text-blue-300">
              Ab dem Plus-Tarif liest die KI Betrag, Datum und Händler aus Foto oder PDF – im Starter-Tarif erfasst du Belege manuell.
            </span>
          </span>
          <Link
            to="/app/einstellungen/tarif"
            className="shrink-0 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-blue-700"
          >
            Tarif ansehen
          </Link>
        </div>
      )}

      {/* Pipeline-Status: immer sichtbar, unabhaengig vom gewaehlten Zeitraum.
          Neu hochgeladene Belege liegen im aktuellen Monat — steht der Nutzer
          in einem anderen Zeitraum, waere die laufende Erkennung sonst
          unsichtbar. Klick filtert genau auf die betroffenen Belege. */}
      {!loading && (pendingCount > 0 || reviewCount > 0 || dupFlags.size > 0 || dupOk.size > 0) && (
        <div className="mb-4 space-y-2">
          {pendingCount > 0 && (
            <button
              type="button"
              onClick={() => setStatusFilter(statusFilter === 'pending' ? '' : 'pending')}
              aria-pressed={statusFilter === 'pending'}
              className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition ${
                statusFilter === 'pending'
                  ? 'border-blue-500 bg-blue-100 ring-2 ring-blue-200 dark:border-blue-500 dark:bg-blue-900/50 dark:ring-blue-800'
                  : 'border-blue-200 bg-blue-50 hover:bg-blue-100 dark:border-blue-900 dark:bg-blue-950/40 dark:hover:bg-blue-900/40'
              }`}
            >
              <Loader2 className="h-5 w-5 shrink-0 animate-spin text-blue-600 dark:text-blue-400" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-blue-900 dark:text-blue-100">
                  {pendingCount} {pendingCount === 1 ? 'Beleg wird' : 'Belege werden'} von der KI erkannt …
                </span>
                <span className="block text-xs text-blue-700 dark:text-blue-300">
                  Läuft im Hintergrund – die Seite aktualisiert sich automatisch.
                  {statusFilter === 'pending' ? ' Klicken, um wieder alle zu zeigen.' : ' Klicken, um sie anzuzeigen.'}
                </span>
              </span>
            </button>
          )}
          {reviewCount > 0 && (
            <button
              type="button"
              onClick={() => setStatusFilter(statusFilter === 'review' ? '' : 'review')}
              aria-pressed={statusFilter === 'review'}
              className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition ${
                statusFilter === 'review'
                  ? 'border-amber-500 bg-amber-100 ring-2 ring-amber-200 dark:border-amber-600 dark:bg-amber-900/50 dark:ring-amber-800'
                  : 'border-amber-200 bg-amber-50 hover:bg-amber-100 dark:border-amber-900 dark:bg-amber-950/40 dark:hover:bg-amber-900/40'
              }`}
            >
              <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-amber-900 dark:text-amber-100">
                  {reviewCount} {reviewCount === 1 ? 'Beleg braucht' : 'Belege brauchen'} deine Prüfung
                </span>
                <span className="block text-xs text-amber-700 dark:text-amber-300">
                  Die KI konnte hier nicht alles lesen – bitte Betrag und Datum ergänzen.
                  {statusFilter === 'review' ? ' Klicken, um wieder alle zu zeigen.' : ' Klicken, um sie anzuzeigen.'}
                </span>
              </span>
            </button>
          )}
          {dupFlags.size > 0 && (
            <button
              type="button"
              onClick={() => setStatusFilter(statusFilter === 'duplicate' ? '' : 'duplicate')}
              aria-pressed={statusFilter === 'duplicate'}
              className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition ${
                statusFilter === 'duplicate'
                  ? 'border-orange-500 bg-orange-100 ring-2 ring-orange-200 dark:border-orange-600 dark:bg-orange-900/50 dark:ring-orange-800'
                  : 'border-orange-200 bg-orange-50 hover:bg-orange-100 dark:border-orange-900 dark:bg-orange-950/40 dark:hover:bg-orange-900/40'
              }`}
            >
              <Copy className="h-5 w-5 shrink-0 text-orange-600 dark:text-orange-400" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-orange-900 dark:text-orange-100">
                  {dupFlags.size} {dupFlags.size === 1 ? 'Beleg könnte doppelt erfasst sein' : 'Belege könnten doppelt erfasst sein'}
                </span>
                <span className="block text-xs text-orange-700 dark:text-orange-300">
                  Gleicher Händler, Betrag und Datum bzw. gleiche Rechnungsnummer – bitte prüfen und ggf. löschen.
                  {statusFilter === 'duplicate' ? ' Klicken, um wieder alle zu zeigen.' : ' Klicken, um sie zu vergleichen.'}
                </span>
              </span>
            </button>
          )}
          {/* Abgenickte Kombinationen: Hinweis + Weg zurueck. Sie zeigen per
              Definition kein Badge mehr, also braucht es die Sammelaktion hier. */}
          {dupOk.size > 0 && (
            <div className="flex items-center justify-between gap-3 px-1 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1.5">
                <CheckCheck className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                {dupOk.size} als „kein Duplikat“ gemerkt – ein erneuter Upload desselben Belegs wird weiterhin erkannt.
              </span>
              <button
                type="button"
                onClick={() => void resetDupOk()}
                className="shrink-0 font-medium text-blue-600 hover:underline dark:text-blue-400"
              >
                Zurücksetzen
              </button>
            </div>
          )}
        </div>
      )}

      {/* Suche + Zeitraum (Monat/Jahr/Alle) + Richtungsfilter — nur wenn Belege da sind. */}
      {!loading && receipts.length > 0 && (
        <div className="mb-4 space-y-3">
          <ReceiptSearch
            value={query}
            onChange={setQuery}
            suggestions={suggestions}
            onPick={(v) => { setQuery(v); setRange('all'); }}
          />
          <div className="flex flex-wrap items-center gap-3">
            {/* Zeitraum-Umschalter: Monat | Jahr | Alle */}
            <div className={`flex items-center rounded-lg border border-gray-300 p-0.5 dark:border-gray-700 ${statusFilter ? 'opacity-50' : ''}`}>
              {([['month', 'Monat'], ['year', 'Jahr'], ['all', 'Alle']] as const).map(([val, label]) => (
                <button
                  key={val}
                  type="button"
                  // Klick hebt einen aktiven Statusfilter auf — sonst haette der
                  // Zeitraum keine sichtbare Wirkung.
                  onClick={() => { setRange(val); setStatusFilter(''); }}
                  aria-pressed={!statusFilter && range === val}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                    !statusFilter && range === val
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Vor/Zurück + Direktsprung — bei 'Alle' gibt es nichts zu blättern. */}
            {range !== 'all' && cursor && (
              <div className="flex items-center gap-1 rounded-lg border border-gray-300 dark:border-gray-700">
                <button
                  type="button"
                  onClick={goOlder}
                  disabled={!hasOlder}
                  aria-label={range === 'year' ? 'Älteres Jahr' : 'Älterer Monat'}
                  className="flex h-9 w-9 items-center justify-center rounded-l-lg text-gray-600 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-300 dark:text-gray-300 dark:hover:bg-gray-700 dark:disabled:text-gray-600"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <div className="relative min-w-[8.5rem]">
                  <span className="pointer-events-none block px-2 py-2 text-center text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {cursorLabel || '—'}
                  </span>
                  {/* Unsichtbares Select ueber dem Label: ein Klick -> direkt zu beliebigem Monat/Jahr springen. */}
                  <select
                    value={cursorStep}
                    onChange={(e) => { if (e.target.value) jumpTo(e.target.value); }}
                    aria-label={range === 'year' ? 'Jahr auswählen' : 'Monat auswählen'}
                    title={range === 'year' ? 'Jahr wählen' : 'Monat/Jahr wählen'}
                    className="absolute inset-0 h-full w-full cursor-pointer appearance-none bg-transparent text-center text-transparent opacity-0"
                  >
                    {steps.map((s) => (
                      <option key={s} value={s} className="text-gray-900">
                        {range === 'year' ? s : `${MONTHS[Number(s.slice(5, 7)) - 1]} ${s.slice(0, 4)}`}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={goNewer}
                  disabled={!hasNewer}
                  aria-label={range === 'year' ? 'Neueres Jahr' : 'Neuerer Monat'}
                  className="flex h-9 w-9 items-center justify-center rounded-r-lg text-gray-600 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-300 dark:text-gray-300 dark:hover:bg-gray-700 dark:disabled:text-gray-600"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            )}
            <select
              value={filterDir}
              onChange={(e) => setFilterDir(e.target.value as '' | 'income' | 'expense')}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            >
              <option value="">Einnahmen & Ausgaben</option>
              <option value="income">Nur Einnahmen</option>
              <option value="expense">Nur Ausgaben</option>
            </select>
            <span className="w-full text-sm text-gray-500 sm:ml-auto sm:w-auto dark:text-gray-400">
              {visible.length} {visible.length === 1 ? 'Beleg' : 'Belege'} · Saldo{' '}
              <span className={`font-semibold ${visibleSum >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                {visibleSum >= 0 ? '+' : '−'}{euro(Math.abs(visibleSum))}
              </span>
            </span>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-gray-500">Lädt …</p>
      ) : receipts.length === 0 ? (
        <EmptyState
          title="Noch keine Belege"
          hint={
            canUseAi
              ? 'Lade ein Foto oder PDF hoch – die KI erkennt den Rest. Oder erfasse den Beleg manuell.'
              : 'Erfasse deinen ersten Beleg – Rechnung, Quittung oder Einnahme.'
          }
          action={<Button onClick={startNew}>+ Neuer Beleg</Button>}
        />
      ) : visible.length === 0 && statusFilter ? (
        <EmptyState
          title="Keine Belege in dieser Auswahl"
          hint="Der zusätzliche Filter (Einnahmen/Ausgaben oder Suche) schließt alle betroffenen Belege aus."
          action={<Button onClick={() => { setStatusFilter(''); setFilterDir(''); setQuery(''); }}>Filter zurücksetzen</Button>}
        />
      ) : visible.length === 0 && terms.length > 0 ? (
        <EmptyState
          title={`Keine Treffer für „${query.trim()}“`}
          hint={
            range === 'all'
              ? 'Kein Beleg enthält alle Suchbegriffe. Probiere einen kürzeren Begriff.'
              : `Es wurde nur ${cursorLabel} durchsucht. Erweitere den Zeitraum auf „Alle“, um alle Belege zu durchsuchen.`
          }
          action={
            range === 'all'
              ? <Button variant="secondary" onClick={() => setQuery('')}>Suche zurücksetzen</Button>
              : <Button onClick={() => setRange('all')}>Alle Belege durchsuchen</Button>
          }
        />
      ) : visible.length === 0 ? (
        <EmptyState
          title={`Keine ${filterDir === 'income' ? 'Einnahmen' : filterDir === 'expense' ? 'Ausgaben' : 'Belege'}${range === 'all' ? '' : ` in ${cursorLabel}`}`}
          hint={
            range === 'all'
              ? 'Für diese Auswahl gibt es keine Belege. Ändere die Art.'
              : `In ${range === 'year' ? 'diesem Jahr' : 'diesem Monat'} gibt es dazu keine Belege. Blättere weiter, wechsle den Zeitraum oder ändere die Art.`
          }
          action={filterDir ? <Button variant="secondary" onClick={() => setFilterDir('')}>Einnahmen & Ausgaben zeigen</Button> : undefined}
        />
      ) : (
        <div className="space-y-2">
          {shown.map((r) => (
            <Card key={r.id} className="flex items-center justify-between !p-4">
              <button
                type="button"
                onClick={() => openEdit(r)}
                className="-m-1 flex min-w-0 flex-1 items-center gap-3 cursor-pointer rounded-lg p-1 text-left transition hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <MerchantBadge
                  name={r.vendor || r.description || 'Beleg'}
                  logo={r.vendor ? logos[merchantKey(r.vendor)] : null}
                />
                <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-2 font-medium text-gray-900 dark:text-gray-100">
                  <span className="truncate">
                    {r.ocrStatus === 'pending' ? 'Wird erkannt …' : r.vendor || r.description || (r.ocrStatus === 'needs_review' ? 'Nicht erkannter Beleg' : 'Beleg')}
                  </span>
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                      r.direction === 'income'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {r.direction === 'income' ? 'Einnahme' : 'Ausgabe'}
                  </span>
                  {r.isPrivate && <span className="shrink-0 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">Privat</span>}
                  {dupFlags.has(r.id) && (
                    <span
                      title={dupFlags.get(r.id)?.reason}
                      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/40 dark:text-orange-300"
                    >
                      <Copy className="h-3 w-3" />
                      Evtl. doppelt
                    </span>
                  )}
                  {r.ocrStatus === 'pending' && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700"><span className="h-2 w-2 animate-pulse rounded-full bg-blue-500" />KI erkennt …</span>}
                  {r.ocrStatus === 'failed' && <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">Erkennung fehlgeschlagen</span>}
                  {r.ocrStatus === 'needs_review' && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">Nicht erkannt – bitte prüfen</span>}
                </div>
                {r.description && r.ocrStatus !== 'pending' && (
                  <div className="truncate text-sm text-gray-600 dark:text-gray-400">{r.description}</div>
                )}
                <div className="truncate text-sm text-gray-500 dark:text-gray-400">
                  {new Date(r.date).toLocaleDateString('de-DE')}
                  {r.invoiceNumber ? ` · Nr. ${r.invoiceNumber}` : ''}
                  {r.accountNumber ? ` · ${r.accountNumber} ${r.category ?? ''}`.trimEnd() : r.category ? ` · ${r.category}` : ''}
                </div>
                </div>
              </button>
              <div className="flex shrink-0 items-center gap-2 pl-2 sm:pl-3">
                <span className={`whitespace-nowrap font-semibold ${r.direction === 'income' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                  {r.direction === 'income' ? '+' : '−'}{euro(r.grossCents)}
                </span>
                {/* Aktionen: auf Mobile vertikal gestapelt (spart horizontalen Platz),
                    ab sm wieder nebeneinander (genug Breite). */}
                <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-1">
                  {/* "Ist kein Duplikat" — direkt aus der Liste abnickbar, ohne
                      den Beleg zu oeffnen. Liegt bewusst hier und nicht am
                      Badge: das Badge steckt im Klickbereich, der den Editor
                      oeffnet (verschachtelte Buttons sind ungueltig). */}
                  {dupFlags.has(r.id) && (
                    <button
                      type="button"
                      onClick={() => void markNotDuplicate(r.id)}
                      aria-label="Ist kein Duplikat"
                      title="Ist kein Duplikat – nur für diese Kombination. Ein erneuter Upload desselben Belegs wird weiter erkannt."
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-orange-500 transition hover:bg-orange-50 hover:text-orange-700 sm:h-9 sm:w-9 dark:hover:bg-gray-700"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => openEdit(r)}
                    aria-label="Bearbeiten"
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-500 transition hover:bg-blue-50 hover:text-blue-600 sm:h-9 sm:w-9 dark:hover:bg-gray-700"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(r.id)}
                    aria-label="Löschen"
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-500 transition hover:bg-red-50 hover:text-red-600 sm:h-9 sm:w-9 dark:hover:bg-gray-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
          {hiddenCount > 0 && (
            <div className="pt-2 text-center">
              <Button variant="secondary" onClick={() => setLimit((n) => n + PAGE_SIZE)}>
                Weitere {Math.min(hiddenCount, PAGE_SIZE)} von {hiddenCount} Belegen anzeigen
              </Button>
            </div>
          )}
        </div>
      )}

      {draft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={closeEditor}>
          <div className="relative max-h-[90vh] w-full max-w-lg overflow-auto rounded-2xl bg-white p-6 dark:bg-gray-800" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={closeEditor}
              aria-label="Schließen"
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700"
            >
              ✕
            </button>
            <h2 className="mb-4 pr-8 text-lg font-bold text-gray-900 dark:text-gray-100">{editId ? 'Beleg bearbeiten' : 'Neuer Beleg'}</h2>
            <div className="space-y-4">
              <ErrorNote error={error} />

              {/* Original-Beleg (Bild) als Vorschau zum Abgleich */}
              {/* Vorschau des Originalbelegs. PDFs koennen nicht in ein <img>,
                  deshalb ein <iframe>; darunter ein Link zum Oeffnen in voller
                  Groesse (im iframe ist die Anzeige klein und ohne Bedienung). */}
              {fileUrl && (
                fileMime === 'application/pdf' ? (
                  <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                    <iframe
                      src={`${fileUrl}#toolbar=0&navpanes=0&view=FitH`}
                      title="Beleg (PDF)"
                      className="h-72 w-full bg-gray-50 dark:bg-gray-900"
                    />
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="block border-t border-gray-200 bg-gray-50 px-3 py-2 text-center text-xs font-medium text-blue-600 transition hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-900 dark:text-blue-400 dark:hover:bg-gray-800"
                    >
                      PDF in voller Größe öffnen
                    </a>
                  </div>
                ) : (
                  <a href={fileUrl} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                    <img src={fileUrl} alt="Beleg" className="max-h-56 w-full bg-gray-50 object-contain dark:bg-gray-900" />
                  </a>
                )
              )}

              {/* Kein Original vorhanden (z. B. aus dem Journal-Import uebernommen).
                  Ohne Hinweis sieht das aus, als sei die Vorschau kaputt. */}
              {editId && !fileUrl && !fileMime && (
                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
                  Zu diesem Beleg ist kein Original hinterlegt — er wurde ohne Datei erfasst
                  (z.&nbsp;B. beim Import aus der alten Buchhaltung). Die Buchung bleibt gültig;
                  den Originalbeleg bewahrst du wie bisher auf.
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                {(['expense', 'income'] as const).map((d) => {
                  const selected = draft.direction === d;
                  const income = d === 'income';
                  const sel = income
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700 ring-2 ring-emerald-200 dark:border-emerald-500 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-800'
                    : 'border-rose-500 bg-rose-50 text-rose-700 ring-2 ring-rose-200 dark:border-rose-500 dark:bg-rose-900/30 dark:text-rose-300 dark:ring-rose-800';
                  const unsel = income
                    ? 'border-gray-300 text-emerald-600 hover:border-emerald-400 dark:border-gray-700 dark:text-emerald-400'
                    : 'border-gray-300 text-rose-600 hover:border-rose-400 dark:border-gray-700 dark:text-rose-400';
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setDraft({ ...draft, direction: d })}
                      className={`rounded-xl border px-4 py-2.5 text-sm font-medium transition ${selected ? sel : unsel}`}
                    >
                      {income ? 'Einnahme' : 'Ausgabe'}
                    </button>
                  );
                })}
              </div>

              <Field label="Datum" type="date" value={draft.date} onChange={(v) => setDraft({ ...draft, date: v })} />
              <Field label="Von wem / Lieferant" value={draft.vendor} onChange={(v) => setDraft({ ...draft, vendor: v })} placeholder="z. B. Bürobedarf GmbH" />
              {draft.direction === 'expense' && draft.vendor.trim() && (
                <p className="-mt-2 text-xs text-gray-400">
                  {supplierLinked
                    ? '✓ Als Lieferant unter Kontakten verknüpft.'
                    : 'Wird automatisch als Lieferant unter Kontakten angelegt/verknüpft.'}
                </p>
              )}
              <Field label="Rechnungsnummer" value={draft.invoiceNumber} onChange={(v) => setDraft({ ...draft, invoiceNumber: v })} placeholder="z. B. RE-2026-0042" />
              <Field label="Was wurde gekauft (Beschreibung)" value={draft.description} onChange={(v) => setDraft({ ...draft, description: v })} placeholder="z. B. Bohrmaschine, Schrauben, Dübel" />

              {/* Live-Hinweis auf moegliche Dubletten (warnt, blockiert nicht). */}
              {dupWarning.length > 0 && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                  <p className="font-medium">⚠️ Möglicherweise bereits erfasst</p>
                  <ul className="mt-1 space-y-0.5 text-xs">
                    {dupWarning.slice(0, 3).map((dup) => (
                      <li key={dup.id}>
                        {dup.vendor ?? 'Beleg'}{dup.invoiceNumber ? ` · Nr. ${dup.invoiceNumber}` : ''} · {new Date(dup.date).toLocaleDateString('de-DE')} · {euro(dup.grossCents)} <span className="opacity-70">({dup.reason})</span>
                      </li>
                    ))}
                  </ul>
                  {/* Fehlalarm abnicken — nur bei einem bereits gespeicherten
                      Beleg moeglich (ein neuer hat noch keine ID). Gemerkt wird
                      nur DIESE Kombination, ein spaeterer Doppel-Upload wird
                      weiter erkannt. Schliesst den Editor nicht. */}
                  {editId && (
                    <button
                      type="button"
                      onClick={() => {
                        const ids = [editId, ...dupWarning.map((d) => d.id)];
                        setDupWarning([]);
                        setDupOk((prev) => new Set([...prev, ...dupWarning.map((d) => pairKey(editId, d.id))]));
                        if (active) {
                          receiptApi
                            .setDupOk(active.id, ids, true)
                            .then(() => toast.success('Als „kein Duplikat“ gemerkt.'))
                            .catch((e) => toast.error(errMsg(e)));
                        }
                      }}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-amber-400 px-2.5 py-1 text-xs font-medium text-amber-900 transition hover:bg-amber-100 dark:border-amber-700 dark:text-amber-200 dark:hover:bg-amber-900/40"
                    >
                      <Check className="h-3.5 w-3.5" />
                      Ist kein Duplikat
                    </button>
                  )}
                </div>
              )}

              {/* Positionen — je Zeile Betrag + USt-Satz (für gemischte Sätze) */}
              <div>
                <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Positionen</span>
                <div className="space-y-2">
                  {draft.items.map((it, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input
                        value={it.description}
                        onChange={(e) => setDraft({ ...draft, items: draft.items.map((x, i) => (i === idx ? { ...x, description: e.target.value } : x)) })}
                        placeholder="Beschreibung"
                        className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                      />
                      <input
                        value={it.gross}
                        onChange={(e) => setDraft({ ...draft, items: draft.items.map((x, i) => (i === idx ? { ...x, gross: e.target.value } : x)) })}
                        placeholder="0,00"
                        inputMode="decimal"
                        className="w-20 shrink-0 rounded-lg border border-gray-300 px-2 py-2 text-right text-sm outline-none focus:border-blue-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                      />
                      <select
                        value={it.taxRate}
                        onChange={(e) => setDraft({ ...draft, items: draft.items.map((x, i) => (i === idx ? { ...x, taxRate: e.target.value } : x)) })}
                        className="w-16 shrink-0 rounded-lg border border-gray-300 px-1 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                      >
                        <option value="19">19%</option>
                        <option value="7">7%</option>
                        <option value="0">0%</option>
                      </select>
                      {draft.items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setDraft({ ...draft, items: draft.items.filter((_, i) => i !== idx) })}
                          aria-label="Position entfernen"
                          className="px-1 text-gray-400 hover:text-red-600"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setDraft({ ...draft, items: [...draft.items, emptyItem()] })}
                  className="mt-2 text-sm font-medium text-blue-600 hover:underline"
                >
                  + Position hinzufügen
                </button>
              </div>

              {/* Summen */}
              {(() => {
                const t = draftTotals(draft);
                return (
                  <div className="space-y-0.5 rounded-xl bg-gray-50 p-3 text-right text-sm dark:bg-gray-900">
                    <div className="text-gray-600 dark:text-gray-400">Netto: {euro(t.net)}</div>
                    <div className="text-gray-600 dark:text-gray-400">USt: {euro(t.tax)}</div>
                    <div className="font-bold text-gray-900 dark:text-gray-100">Gesamt: {euro(t.gross)}</div>
                  </div>
                );
              })()}

              {/* Privat: keine Betriebsausgabe/-einnahme (z.B. Medikamente) */}
              <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 p-3 dark:border-gray-700">
                <input
                  type="checkbox"
                  checked={draft.isPrivate}
                  onChange={(e) => setDraft({ ...draft, isPrivate: e.target.checked, ...(e.target.checked ? { accountNumber: '', isBewirtung: false } : {}) })}
                  className="mt-0.5 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">
                  <span className="font-medium text-gray-900 dark:text-gray-100">Privat (keine Betriebsausgabe/-einnahme)</span>
                  <span className="block text-gray-500 dark:text-gray-400">Wird gespeichert, aber NICHT gebucht und NICHT in EÜR/USt berücksichtigt – z. B. privat gekaufte Medikamente.</span>
                </span>
              </label>

              {/* Buchhaltungskonto (SKR) — KI-Vorschlag, vom Nutzer bestätigt/änderbar.
                  Bei privaten Belegen entfällt das Konto (keine Buchung). */}
              {!draft.isPrivate && (
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Buchungskonto (SKR)</span>
                <select
                  value={draft.accountNumber}
                  onChange={(e) => {
                    const num = e.target.value;
                    const acc = accounts.find((a) => a.number === num);
                    setDraft({ ...draft, accountNumber: num, category: acc?.name ?? draft.category });
                  }}
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-base outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                >
                  <option value="">— Kein Konto —</option>
                  {(draft.direction === 'income' ? revenueAccounts : expenseAccounts).map((a) => (
                    <option key={a.id} value={a.number}>
                      {a.number} · {a.name}
                    </option>
                  ))}
                </select>
              </label>
              )}

              {/* Bewirtung: 70/30-Aufteilung */}
              {!draft.isPrivate && draft.direction === 'expense' && (
                <div className="rounded-xl border border-gray-200 p-3">
                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      checked={draft.isBewirtung}
                      onChange={(e) => setDraft({ ...draft, isBewirtung: e.target.checked })}
                      className="mt-0.5 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">
                      <span className="font-medium text-gray-900">Bewirtungskosten (Geschäftsessen)</span>
                      <span className="block text-gray-500">70 % abziehbar / 30 % nicht abziehbar · Vorsteuer zu 100 % abziehbar (§ 4 Abs. 5 Nr. 2 EStG).</span>
                    </span>
                  </label>
                  {draft.isBewirtung && (() => {
                    const t = draftTotals(draft);
                    return (
                      <div className="mt-3 space-y-0.5 rounded-lg bg-amber-50 p-2.5 text-sm text-amber-900">
                        <div>Abziehbar (70 %): <strong>{euro(t.deductibleNet)}</strong> netto</div>
                        <div>Nicht abziehbar (30 %): <strong>{euro(t.nonDeductibleNet)}</strong> netto</div>
                        <div>Vorsteuer (100 %): <strong>{euro(t.tax)}</strong></div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="secondary" onClick={closeEditor}>
                Abbrechen
              </Button>
              <Button onClick={save} busy={busy}>
                Speichern
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
